import orderBy from 'lodash.orderby';

import { permissionsRouteTree } from '@/_shared';
import {
  dashboardWidgetsPermissionConfig as tempDashboardWidgetsPermissionConfig,
  type WidgetModelName,
} from '@/_shared/routes-widgets/configs/permissible-widget-keys';
import type { RouteListItem } from '@/types';

import type { PermissibleRouteKeys, PermissionsActions, PermissionsResourceType, UserRoleFormSchema } from './schemas';

import { settingsBcLink } from '../../model';

export const breadcrumbsLinks: RouteListItem[] = [
  settingsBcLink,
  { path: '/settings/user-roles', name: 'User Roles', auditKey: 'settings/user-roles' },
];

const onePermissions = {
  id: -1,
  role_id: -1,
  action: 'view',
  resource_type: 'model',
  resource: 'team-member',
} as const;

function actionToPermissions(
  key: PermissibleRouteKeys,
  type: PermissionsResourceType,
  permissionsActions: PermissionsActions[]
) {
  return permissionsActions.map((action) => {
    return {
      ...onePermissions,
      id: -1,
      action,
      resource_type: type,
      resource: key,
      value: false,
      index: -1,
    };
  });
}

// Build a permissions model record from the dashboard widgets permission config so we have a single source of truth.
function buildWidgetPermissionModels(): Record<
  WidgetModelName,
  {
    resource: WidgetModelName;
    resource_type: 'widget';
    value: boolean;
    index: number;
  }
> {
  const { mileage_tracking: _, ...dashboardWidgetsPermissionConfig } = tempDashboardWidgetsPermissionConfig;

  const entries = Object.entries(dashboardWidgetsPermissionConfig).map(([key]) => {
    const widgetKey = key as WidgetModelName;
    return [
      widgetKey,
      {
        resource: widgetKey,
        resource_type: 'widget' as const,
        value: false,
        index: -1,
      },
    ];
  });

  return Object.fromEntries(entries) as Record<
    WidgetModelName,
    { resource: WidgetModelName; resource_type: 'widget'; value: boolean; index: number }
  >;
}

const widgetPermissionModels = buildWidgetPermissionModels();

export function buildDefaultPermissions(): UserRoleFormSchema['permissions'] {
  const modelTabsWidgets: UserRoleFormSchema['permissions'] = permissionsRouteTree
    .map((r) => {
      if (r.auditKey === 'dashboard') return [];
      const main = [
        ...actionToPermissions((r?.auditKey ?? 'dashboard') as PermissibleRouteKeys, 'model', [
          'view',
          'create',
          'update',
          'revert',
          'delete',
        ]),
      ];

      if (r.auditKey === 'settings') {
        if (!r) {
          console.log(r);
        }
      }

      const sub = (r.sub.filter((f) => !f.path.endsWith('/$id')) ?? []).map((s) => {
        const pathParts = s.path.split('/');

        return [
          ...actionToPermissions((r.auditKey + '/' + pathParts[pathParts.length - 1]) as PermissibleRouteKeys, 'tab', [
            'view',
          ]),
        ];
      });

      return [main, ...sub].flat();
    })
    .filter((f) => f.length > 0)
    .flat();

  Object.values(widgetPermissionModels).forEach((model) => {
    modelTabsWidgets.push({
      ...onePermissions,
      ...model,
    });
  });

  // !important: id should (index + 1) * -1
  // index is 0 based for form controls
  return orderBy(modelTabsWidgets, 'resource').map((item, index) => ({ ...item, index, id: -1 * (index + 1) }));
}

export const userRolesDefaultValues = {
  id: -1,
  name: '',
  azure_role_id: '',
  permissions: buildDefaultPermissions(),
} as UserRoleFormSchema;
